import React, { useState, useEffect } from 'react';
import adminAPI from '../services/adminAPI';
import useStore from '../store/useStore';
import '../styles/AdminWidgetBuilder.css';

const AdminWidgetBuilder = ({ onClose, onSuccess, editingMetric = null }) => {
  const currentCompany = useStore((state) => state.currentCompany);
  const isEditMode = !!editingMetric;

  const [formData, setFormData] = useState({
    metricKey: '',
    displayName: '',
    description: '',
    widgetType: 'gauge',
    // Widget config
    min: 0,
    max: 100,
    thresholds: '',
    // Simple threshold inputs
    yellowThreshold: '',
    greenThreshold: '',
    useSimpleThresholds: true,
    columns: '',
    dataKey: 'value',
    unit: '',
    // Count widget config
    invertTrendColors: false,
    // Service status config
    healthyMinutes: 5,
    warningMinutes: 15,
    staleMinutes: 30,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Pre-populate form when editing
  useEffect(() => {
    if (editingMetric) {
      const config = editingMetric.config || {};

      // Parse thresholds for simple mode
      let yellowThreshold = '';
      let greenThreshold = '';
      let useSimpleThresholds = true;

      if (config.thresholds && Array.isArray(config.thresholds)) {
        // Try to parse as simple red/yellow/green format
        const sorted = [...config.thresholds].sort((a, b) => a.value - b.value);

        // Check if it matches simple format (2 thresholds with yellow and green colors)
        if (sorted.length === 2) {
          const yellowColors = ['#ffc107', '#ffeb3b', '#ff9800'];
          const greenColors = ['#4caf50', '#8bc34a', '#00e676'];

          const firstIsYellow = yellowColors.includes(sorted[0].color?.toLowerCase());
          const secondIsGreen = greenColors.includes(sorted[1].color?.toLowerCase());

          if (firstIsYellow && secondIsGreen) {
            yellowThreshold = sorted[0].value;
            greenThreshold = sorted[1].value;
          } else {
            useSimpleThresholds = false;
          }
        } else if (sorted.length !== 0) {
          useSimpleThresholds = false;
        }
      }

      setFormData({
        metricKey: editingMetric.metric_key || '',
        displayName: editingMetric.display_name || '',
        description: editingMetric.description || '',
        widgetType: editingMetric.widget_type || 'gauge',
        // Widget config
        min: config.min || 0,
        max: config.max || 100,
        thresholds: config.thresholds ? JSON.stringify(config.thresholds, null, 2) : '',
        yellowThreshold,
        greenThreshold,
        useSimpleThresholds,
        columns: config.columns ? config.columns.join(', ') : '',
        dataKey: config.dataKey || 'value',
        unit: config.unit || '',
        // Count widget config
        invertTrendColors: config.invertTrendColors || false,
        // Service status config
        healthyMinutes: config.healthyMinutes || 5,
        warningMinutes: config.warningMinutes || 15,
        staleMinutes: config.staleMinutes || 30,
      });
    }
  }, [editingMetric]);

  const widgetTypes = [
    { value: 'gauge', label: 'Gauge (Semi-circular)' },
    { value: 'table', label: 'Table' },
    { value: 'line_chart', label: 'Line Chart' },
    { value: 'area_chart', label: 'Area Chart' },
    { value: 'service_status', label: 'Service Status' },
    { value: 'count', label: 'Simple Count' },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Confirmation dialog
    const action = isEditMode ? 'update' : 'create';
    const confirmMessage = isEditMode
      ? `Are you sure you want to update "${formData.displayName}"? This will affect all users who have this widget on their dashboards.`
      : `Create new widget "${formData.displayName}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build widget config based on type
      const config = {};

      if (formData.widgetType === 'gauge') {
        config.min = parseInt(formData.min);
        config.max = parseInt(formData.max);

        // Handle thresholds
        if (formData.useSimpleThresholds) {
          // Generate thresholds from simple inputs
          const thresholds = [];

          if (formData.yellowThreshold !== '' && formData.yellowThreshold !== null) {
            thresholds.push({
              value: parseFloat(formData.yellowThreshold),
              color: '#ffc107'
            });
          }

          if (formData.greenThreshold !== '' && formData.greenThreshold !== null) {
            thresholds.push({
              value: parseFloat(formData.greenThreshold),
              color: '#4caf50'
            });
          }

          if (thresholds.length > 0) {
            config.thresholds = thresholds;
          }
        } else {
          // Use advanced JSON mode
          if (formData.thresholds) {
            try {
              config.thresholds = JSON.parse(formData.thresholds);
            } catch (e) {
              setError('Invalid JSON format for thresholds');
              setIsSubmitting(false);
              return;
            }
          }
        }
      } else if (formData.widgetType === 'table') {
        if (formData.columns) {
          config.columns = formData.columns.split(',').map(c => c.trim());
        }
      } else if (formData.widgetType === 'line_chart' || formData.widgetType === 'area_chart') {
        config.dataKey = formData.dataKey;
      } else if (formData.widgetType === 'count') {
        if (formData.unit) {
          config.unit = formData.unit;
        }
        config.invertTrendColors = formData.invertTrendColors || false;
      } else if (formData.widgetType === 'service_status') {
        config.healthyMinutes = parseInt(formData.healthyMinutes) || 5;
        config.warningMinutes = parseInt(formData.warningMinutes) || 15;
        config.staleMinutes = parseInt(formData.staleMinutes) || 30;
      }

      const payload = {
        metricKey: formData.metricKey,
        displayName: formData.displayName,
        description: formData.description,
        widgetType: formData.widgetType,
        companyId: currentCompany.id,
        config,
      };

      let response;
      if (isEditMode) {
        // Update existing metric
        response = await adminAPI.updateMetricDefinition(editingMetric.id, payload);
      } else {
        // Create new metric
        response = await adminAPI.createMetricDefinition(payload);
      }

      if (response.success) {
        onSuccess && onSuccess(response.data);
        onClose && onClose();
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} widget:`, error);
      setError(error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} widget definition`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close only if click starts and ends on overlay
  const handleOverlayMouseDown = (e) => {
    if (e.target.className === 'modal-overlay') {
      e.target.dataset.mousedownOnOverlay = 'true';
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay' && e.target.dataset.mousedownOnOverlay === 'true') {
      onClose();
    }
    delete e.target.dataset.mousedownOnOverlay;
  };

  return (
    <div className="modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Widget Definition' : 'Create New Widget Definition'}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="widget-builder-form">
          <div className="form-section">
            <h3>Basic Information</h3>

            <div className="form-group">
              <label>Metric Key *</label>
              <input
                type="text"
                name="metricKey"
                value={formData.metricKey}
                onChange={handleChange}
                placeholder="e.g., orders_processed"
                required
                disabled={isEditMode}
                style={isEditMode ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
              <small>{isEditMode ? 'Cannot change metric key of existing widget' : 'Unique identifier for this metric (used by C# app)'}</small>
            </div>

            <div className="form-group">
              <label>Display Name *</label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="e.g., Orders Processed"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of what this metric represents"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Widget Type *</label>
              <select name="widgetType" value={formData.widgetType} onChange={handleChange} required>
                {widgetTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Widget-specific configuration */}
          <div className="form-section">
            <h3>Widget Configuration</h3>

            {formData.widgetType === 'service_status' && (
              <>
                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Configure time-based status indicators for service health monitoring
                </p>
                <div className="form-group">
                  <label>Healthy Threshold (minutes)</label>
                  <input
                    type="number"
                    name="healthyMinutes"
                    value={formData.healthyMinutes}
                    onChange={handleChange}
                    min="1"
                    placeholder="5"
                  />
                  <small>Show as green/running if updated within this many minutes</small>
                </div>
                <div className="form-group">
                  <label>Warning Threshold (minutes)</label>
                  <input
                    type="number"
                    name="warningMinutes"
                    value={formData.warningMinutes}
                    onChange={handleChange}
                    min="1"
                    placeholder="15"
                  />
                  <small>Show as orange/warning if no update for this many minutes</small>
                </div>
                <div className="form-group">
                  <label>Stale Threshold (minutes)</label>
                  <input
                    type="number"
                    name="staleMinutes"
                    value={formData.staleMinutes}
                    onChange={handleChange}
                    min="1"
                    placeholder="30"
                  />
                  <small>Show as red/stopped if no "Running" status for this many minutes</small>
                </div>
              </>
            )}

            {formData.widgetType === 'gauge' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Minimum Value *</label>
                    <input
                      type="number"
                      name="min"
                      value={formData.min}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Maximum Value *</label>
                    <input
                      type="number"
                      name="max"
                      value={formData.max}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.useSimpleThresholds}
                      onChange={(e) => setFormData({ ...formData, useSimpleThresholds: e.target.checked })}
                      style={{ width: 'auto' }}
                    />
                    Use Simple Color Thresholds
                  </label>
                </div>

                {formData.useSimpleThresholds ? (
                  <>
                    <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Set thresholds for color changes: <span style={{ color: '#f44336' }}>Red</span> → <span style={{ color: '#ffc107' }}>Yellow</span> → <span style={{ color: '#4caf50' }}>Green</span>
                    </p>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Yellow Threshold</label>
                        <input
                          type="number"
                          name="yellowThreshold"
                          value={formData.yellowThreshold}
                          onChange={handleChange}
                          placeholder="e.g., 300"
                        />
                        <small>Value where color changes from red to yellow</small>
                      </div>
                      <div className="form-group">
                        <label>Green Threshold</label>
                        <input
                          type="number"
                          name="greenThreshold"
                          value={formData.greenThreshold}
                          onChange={handleChange}
                          placeholder="e.g., 700"
                        />
                        <small>Value where color changes from yellow to green</small>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="form-group">
                    <label>Thresholds (JSON)</label>
                    <textarea
                      name="thresholds"
                      value={formData.thresholds}
                      onChange={handleChange}
                      placeholder='[{"value": 300, "color": "#ffc107"}, {"value": 700, "color": "#4caf50"}]'
                      rows={3}
                    />
                    <small>JSON array of threshold values and colors</small>
                  </div>
                )}
              </>
            )}

            {formData.widgetType === 'table' && (
              <div className="form-group">
                <label>Column Names (comma-separated) *</label>
                <input
                  type="text"
                  name="columns"
                  value={formData.columns}
                  onChange={handleChange}
                  placeholder="Item, Count"
                  required
                />
              </div>
            )}

            {(formData.widgetType === 'line_chart' || formData.widgetType === 'area_chart') && (
              <div className="form-group">
                <label>Data Key *</label>
                <input
                  type="text"
                  name="dataKey"
                  value={formData.dataKey}
                  onChange={handleChange}
                  placeholder="value"
                  required
                />
                <small>Property name in the data array to plot</small>
              </div>
            )}

            {formData.widgetType === 'count' && (
              <>
                <div className="form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    placeholder="e.g., users, sec, %"
                  />
                  <small>Optional unit to display after the value</small>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="invertTrendColors"
                      checked={formData.invertTrendColors}
                      onChange={(e) => setFormData({ ...formData, invertTrendColors: e.target.checked })}
                      style={{ width: 'auto' }}
                    />
                    Invert Trend Colors
                  </label>
                  <small>When enabled, downward trends show green and upward trends show red (useful for backlogs, errors, etc.)</small>
                </div>
              </>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Widget Definition' : 'Create Widget Definition')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminWidgetBuilder;
